import React, { useState, useEffect } from 'react';

interface CountdownTimerProps {
  targetDate: Date;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState<{ hours: number; minutes: number; seconds: number } | null>(null);
  const [status, setStatus] = useState<'pending' | 'started'>('pending');

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();

      if (difference <= 0) {
        setStatus('started');
        setTimeLeft(null);
        return;
      }

      setTimeLeft({
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      });
      setStatus('pending');
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  if (status === 'started') {
    return (
      <div className="flex items-center text-green-600 font-bold animate-pulse">
        <span className="mr-2">●</span> Starting Now
      </div>
    );
  }

  if (!timeLeft) return null;

  return (
    <div className="flex gap-2 items-center justify-center">
      <div className="flex flex-col items-center p-2 bg-primary-50 rounded-lg min-w-[44px] border border-primary-100">
        <span className="font-mono text-lg font-bold text-primary-700 leading-none">
          {timeLeft.hours.toString().padStart(2, '0')}
        </span>
        <span className="text-[9px] uppercase text-primary-400 font-bold mt-1">Hrs</span>
      </div>
      <span className="text-primary-300 font-bold -mt-3">:</span>
      <div className="flex flex-col items-center p-2 bg-primary-50 rounded-lg min-w-[44px] border border-primary-100">
        <span className="font-mono text-lg font-bold text-primary-700 leading-none">
          {timeLeft.minutes.toString().padStart(2, '0')}
        </span>
        <span className="text-[9px] uppercase text-primary-400 font-bold mt-1">Min</span>
      </div>
      <span className="text-primary-300 font-bold -mt-3">:</span>
      <div className="flex flex-col items-center p-2 bg-primary-50 rounded-lg min-w-[44px] border border-primary-100">
        <span className="font-mono text-lg font-bold text-primary-700 leading-none">
          {timeLeft.seconds.toString().padStart(2, '0')}
        </span>
        <span className="text-[9px] uppercase text-primary-400 font-bold mt-1">Sec</span>
      </div>
    </div>
  );
};

export default CountdownTimer;
